import { useState, useEffect, useCallback } from 'react'
import {
  Loader2, Clock, CheckCircle, XCircle, TimerOff, Ban, Send, Undo2, RefreshCw, Download,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { fetchAssignments, offerPickup, revokePickupOffer } from '../services/adminService'
import { exportToCsv } from '../utils/exportCsv'
import EscalationQueue from '../components/EscalationQueue'
import type { PickupAssignment, AssignmentStatus, OfferConfig, OfferTrend } from '../types'

const STATUS_TABS: { value: AssignmentStatus | 'ALL'; label: string }[] = [
  { value: 'OFFERED', label: 'Awaiting reply' },
  { value: 'ACCEPTED', label: 'Accepted' },
  { value: 'DECLINED', label: 'Declined' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'REVOKED', label: 'Revoked' },
  { value: 'ALL', label: 'All' },
]

const STATUS_STYLES: Record<string, { bg: string; text: string; icon: typeof Clock }> = {
  OFFERED: { bg: 'bg-orange-100', text: 'text-orange-700', icon: Clock },
  ACCEPTED: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
  DECLINED: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
  EXPIRED: { bg: 'bg-gray-100', text: 'text-gray-600', icon: TimerOff },
  REVOKED: { bg: 'bg-purple-100', text: 'text-purple-700', icon: Ban },
}

/**
 * How long is left on an offer, or null once it has lapsed.
 *
 * Windows run to thirteen hours now, so this cannot be a minute count: "780m
 * left" is not something an admin can read at a glance.
 */
function timeLeft(expiresAt: string): { label: string; minutes: number } | null {
  const diff = new Date(expiresAt).getTime() - Date.now()
  if (diff <= 0) return null
  const minutes = Math.max(1, Math.round(diff / 60_000))
  if (minutes < 60) return { label: `${minutes}m left`, minutes }
  const hours = Math.floor(minutes / 60)
  return { label: `${hours}h ${minutes % 60}m left`, minutes }
}

/** Turn a minute count from the server into copy, e.g. 720 -> "12 hours". */
function humaniseMinutes(total: number): string {
  if (total < 60) return `${total} minutes`
  const hours = Math.round((total / 60) * 10) / 10
  return `${hours} hour${hours === 1 ? '' : 's'}`
}

function relativeTime(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

function StatusPill({ status }: { status: AssignmentStatus }) {
  const style = STATUS_STYLES[status] ?? STATUS_STYLES.EXPIRED
  const Icon = style.icon
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${style.bg} ${style.text}`}>
      <Icon size={10} />
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  )
}

export default function Assignments() {
  const [assignments, setAssignments] = useState<PickupAssignment[]>([])
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [config, setConfig] = useState<OfferConfig>({
    offerTtlMinutes: 13 * 60,
    remindAfterMinutes: 12 * 60,
    graceMinutes: 60,
    maxOfferRounds: 3,
    shortlistSize: 3,
    leadMinutes: 120,
  })
  const [trend, setTrend] = useState<OfferTrend | null>(null)
  const [status, setStatus] = useState<AssignmentStatus | 'ALL'>('OFFERED')
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchAssignments({ status, limit: 100 })
      setAssignments(data?.assignments ?? [])
      setCounts(data?.counts ?? {})
      setTrend(data?.trend ?? null)
      // Merge, not replace: a backend from before a field was added would
      // otherwise wipe its default and the paragraph below would read "NaN".
      if (data?.config) setConfig((current) => ({ ...current, ...data.config }))
    } catch {
      toast.error('Failed to load the assignment queue')
    } finally {
      setLoading(false)
    }
  }, [status])

  useEffect(() => { load() }, [load])

  // Offers lapse on a timer, so a queue left open eventually goes stale. Five
  // minutes rather than one: the window is hours long, and the countdown below
  // is recomputed on every render anyway.
  useEffect(() => {
    if (status !== 'OFFERED') return
    const timer = setInterval(load, 5 * 60_000)
    return () => clearInterval(timer)
  }, [status, load])

  const handleRevoke = async (assignment: PickupAssignment, reoffer: boolean) => {
    setBusyId(assignment.id)
    try {
      const result = await revokePickupOffer(assignment.pickupId, reoffer)
      toast.success(
        reoffer && result?.reoffered
          ? 'Offer withdrawn and passed to another collector.'
          : 'Offer withdrawn. The pickup is unassigned.',
      )
      load()
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast.error(message || 'Failed to withdraw the offer')
    } finally {
      setBusyId(null)
    }
  }

  const handleOffer = async (assignment: PickupAssignment) => {
    setBusyId(assignment.id)
    try {
      await offerPickup(assignment.pickupId)
      toast.success('Offered to the next best-matched collector.')
      load()
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast.error(message || 'Failed to offer the pickup')
    } finally {
      setBusyId(null)
    }
  }

  // From the trend window, not all time: an acceptance rate averaged over the
  // whole history of the platform cannot tell you whether a change made things
  // better, which is the only reason to look at it.
  const acceptanceRate =
    trend && trend.settled > 0 ? Math.round((trend.accepted / trend.settled) * 1000) / 10 : 0

  return (
    <div className="space-y-5">
      {/* What needs a person, before what is running itself. */}
      <EscalationQueue onResolved={load} />

      {/* Summary. The first two are now; the last two are the last week, which
          is the only thing that says whether the response window is right. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {[
          {
            label: 'Awaiting reply',
            value: counts.OFFERED ?? 0,
            tone: 'text-orange-600',
            note: 'collectors deciding right now',
          },
          {
            label: 'Waiting on an admin',
            value: trend?.waitingOnAdmin ?? 0,
            tone: (trend?.waitingOnAdmin ?? 0) > 0 ? 'text-red-600' : 'text-[#1A1A2E]',
            note: 'escalated and still unplaced',
          },
          {
            label: `Accepted (${trend?.days ?? 7}d)`,
            value: trend ? `${acceptanceRate}%` : '—',
            tone: 'text-green-700',
            note: trend ? `${trend.accepted} of ${trend.settled} offers` : '',
          },
          {
            label: `No reply (${trend?.days ?? 7}d)`,
            value: trend ? `${trend.ignoredRate}%` : '—',
            // A high share of offers nobody ever answered is the signal that the
            // window is wrong or that particular collectors are dead weight. It
            // is the number worth looking at, so it is allowed to shout.
            tone: (trend?.ignoredRate ?? 0) >= 25 ? 'text-red-600' : 'text-[#6B7280]',
            note: trend ? `${trend.ignored} never answered` : '',
          },
        ].map(({ label, value, tone, note }) => (
          <div key={label} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <p className="text-xs text-[#6B7280] font-medium">{label}</p>
            <p className={`text-2xl font-bold mt-1 ${tone}`}>{value}</p>
            {note && <p className="text-[10px] text-[#9CA3AF] mt-0.5">{note}</p>}
          </div>
        ))}
      </div>

      <p className="text-xs text-[#6B7280]">
        A pickup is offered to one collector at a time. They get{' '}
        <strong>{humaniseMinutes(config.remindAfterMinutes)}</strong> to answer, are then reminded
        by app, push and email, and have <strong>{humaniseMinutes(config.graceMinutes)}</strong>{' '}
        more. If they still say nothing it is passed to the next collector who can be reached — as
        long as rounds remain and a fresh window would still end{' '}
        <strong>{humaniseMinutes(config.leadMinutes)}</strong> before the pickup; otherwise it goes
        to the escalation queue above with the {config.shortlistSize} nearest alternatives. A
        decline passes it on immediately. Every window ends{' '}
        <strong>{humaniseMinutes(config.leadMinutes)}</strong> before the pickup and is shortened
        for one due sooner.
      </p>

      {/* Filters */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          {STATUS_TABS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setStatus(value)}
              className={`px-3 py-2 text-xs font-semibold transition-colors ${
                status === value ? 'bg-primary-600 text-white' : 'text-[#6B7280] hover:bg-gray-50'
              }`}
            >
              {label}
              {value !== 'ALL' && counts[value] ? ` (${counts[value]})` : ''}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            className="flex items-center gap-1.5 text-xs font-semibold text-[#1A1A2E] bg-gray-100 px-3 py-2 rounded-xl hover:bg-gray-200 transition-colors"
          >
            <RefreshCw size={13} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => {
              if (!assignments.length) return
              exportToCsv(
                ['Pickup', 'Address', 'Collector', 'Status', 'Round', 'Distance km', 'Offered', 'Reminded', 'Expires', 'Responded', 'Reason'],
                assignments.map((a) => [
                  a.pickupId.slice(0, 8), a.pickup?.address ?? '', a.collector?.name ?? '',
                  a.status, String(a.round), a.distanceKm != null ? String(a.distanceKm) : '',
                  a.offeredAt, a.remindedAt ?? '', a.expiresAt, a.respondedAt ?? '', a.declineReason ?? '',
                ]),
                'assignments',
              )
            }}
            className="flex items-center gap-1.5 text-xs font-semibold text-[#1A1A2E] bg-gray-100 px-3 py-2 rounded-xl hover:bg-gray-200 transition-colors"
          >
            <Download size={13} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : assignments.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-sm font-semibold text-[#6B7280]">
              {status === 'OFFERED' ? 'No offers awaiting a reply' : 'Nothing here'}
            </p>
            <p className="text-xs text-[#9CA3AF] mt-1">
              {status === 'OFFERED'
                ? 'Every pending pickup has either been taken or has nobody to offer it to.'
                : 'Try another status.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#F8FAF9]">
                  {['Pickup', 'Collector', 'Round', 'Distance', 'Offered', 'Status', 'Actions'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {assignments.map((a) => {
                  const remaining = a.status === 'OFFERED' ? timeLeft(a.expiresAt) : null
                  return (
                    <tr key={a.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-xs font-mono font-semibold text-primary-700">
                          {a.pickupId.slice(0, 8)}
                        </p>
                        <p className="text-xs text-[#6B7280] max-w-[220px] truncate" title={a.pickup?.address}>
                          {a.pickup?.address ?? '—'}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs font-medium text-[#1A1A2E]">{a.collector?.name ?? '—'}</p>
                        {a.collector?.phone && (
                          <p className="text-[10px] text-[#6B7280]">{a.collector.phone}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#6B7280]">
                        {a.round} / {config.maxOfferRounds}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#6B7280] whitespace-nowrap">
                        {a.distanceKm != null ? `${a.distanceKm} km` : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#6B7280] whitespace-nowrap">
                        {relativeTime(a.offeredAt)}
                        {a.status === 'OFFERED' && (
                          <span
                            className={`block text-[10px] font-semibold ${
                              remaining != null && remaining.minutes <= config.graceMinutes
                                ? 'text-red-600'
                                : 'text-orange-600'
                            }`}
                          >
                            {remaining != null ? remaining.label : 'lapsing'}
                          </span>
                        )}
                        {a.status === 'OFFERED' && a.remindedAt && (
                          <span className="block text-[10px] text-amber-700">reminded</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <StatusPill status={a.status} />
                        {a.declineReason && (
                          <p className="text-[10px] text-[#9CA3AF] mt-1 max-w-[160px] truncate" title={a.declineReason}>
                            {a.declineReason}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {a.status === 'OFFERED' ? (
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              title="Withdraw and pass to the next collector"
                              disabled={busyId === a.id}
                              onClick={() => handleRevoke(a, true)}
                              className="flex items-center gap-1 text-xs text-primary-700 hover:bg-primary-50 px-2 py-1 rounded-lg transition disabled:opacity-50"
                            >
                              <Send className="w-3.5 h-3.5" />
                              Pass on
                            </button>
                            <button
                              type="button"
                              title="Withdraw the offer and leave the pickup unassigned"
                              disabled={busyId === a.id}
                              onClick={() => handleRevoke(a, false)}
                              className="flex items-center gap-1 text-xs text-red-600 hover:bg-red-50 px-2 py-1 rounded-lg transition disabled:opacity-50"
                            >
                              <Undo2 className="w-3.5 h-3.5" />
                              Withdraw
                            </button>
                          </div>
                        ) : a.pickup?.status === 'PENDING' ? (
                          <button
                            type="button"
                            title="Offer this pickup again"
                            disabled={busyId === a.id}
                            onClick={() => handleOffer(a)}
                            className="flex items-center gap-1 text-xs text-primary-700 hover:bg-primary-50 px-2 py-1 rounded-lg transition disabled:opacity-50"
                          >
                            <Send className="w-3.5 h-3.5" />
                            Offer again
                          </button>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
